<script setup lang="ts">
import { DisplayType, FormDesignItemDto, FormSectionDesignDto } from '@/core/store/survey/surveyDesignDtos';
import Survey from '@/core/store/survey/surveyStore';
import { computed, onBeforeMount, ref, watch, onMounted, onUnmounted, CSSProperties } from 'vue';
import SurveyItem from './surveyItem.vue';

import bannerIcon from '/assets/bannerIcon.svg';
import trashIcon from '/assets/trash.svg';
import { Sh } from '@/core/sharedService';

const { itemsOrganized, 
        displayMode, 
        selectItem, 
        selectedSectionId, 
        selectedItemId, 
        isDesign, 
        event, 
        currentDesign, 
        setDesign 
      } = Survey();

const props = defineProps({
  section: {
    type: Object as () => FormSectionDesignDto,
    required: true
  }
});
const onClickLabel = () => {
  if (displayMode.value == DisplayType.EDIT) {
    isEditingLabel.value = true;
  }
}
const onKeyLabelEdit = (event) => {
  if (event.key === 'Enter') {

    setLabel(event.target.value);
    endEdit();
  }
}
const setLabel = (label: string) => {
  props.section.section.label = label;
  isEditingLabel.value = false;
}
const endEdit = () => {
  isEditingLabel.value = false;
}
const initDeployed = () => {
  if (selectedItemId.value == props.section.section.id) return true;
  if ([DisplayType.DISPLAY, DisplayType.PDF].includes(displayMode.value)) return true;
  return false;
};
const isEditingLabel = ref<boolean>(false)
const isDeployed = ref<boolean>(initDeployed());
const isSelected = computed(() => {
  if (selectedItemId.value == section.value.id) return true;
  return false;
})
const canDelete = computed(() => {
  return !currentDesign.value.cab.hasResponsesClosed;
})
const section = computed<FormDesignItemDto>(() => {
  return props.section.section;
})

const classComponent = computed<string>(() => {
  let clss: string[] = ["survey-base-content"];
  if (isDesign) clss.push("survey-base-content-design");
  return clss.join(" ");
})

watch(itemsOrganized, (newValue, oldValue) => {
  let idSectionChangeItem: string = null
  let x = 0;
  for (const i of <FormSectionDesignDto[]>newValue) {
    if ((<FormSectionDesignDto[]>oldValue)[x] && (<FormSectionDesignDto[]>oldValue)[x].items.length !== i.items.length) {
      idSectionChangeItem = i.section.id;
    }
    x++;
  }
  if (idSectionChangeItem == section.value.id) {
    isDeployed.value = true;
  }
});

onMounted(() => {
  event.on("deploy", deploy);
});
onUnmounted(() => {
  event.off("deploy", deploy);
})
onBeforeMount(() => {
  if (selectedSectionId.value == props.section.section.id) {
    isDeployed.value = true;
  }
});
const toggleSection = () => {
  if (displayMode.value == DisplayType.PDF && !isDesign.value) return;
  isDeployed.value = !isDeployed.value;
  selectItem(section.value.id);
}
const deploy = (dep: boolean = true) => {
  isDeployed.value = dep;
}

const sectionStyle = computed<CSSProperties>(() => {
  const sect = section.value;
  const currentStyle = displayMode.value == DisplayType.DISPLAY ? sect.properties.screen.labelStyle : (displayMode.value == DisplayType.EDIT ? sect.properties.input.labelStyle : sect.properties.print.labelStyle);
  let css: CSSProperties = {
    width: currentStyle.width ? `${currentStyle.width}%` : "100%",
    minHeight: "40px",
    fontSize: Sh.getFontSize(currentStyle.fontSize, 16),
    color: currentStyle.color,
    backgroundColor: currentStyle.backgroundColor,
    textAlign: currentStyle.align,
    fontWeight: currentStyle.bold ? 'bold' : 'normal',
    paddingLeft: "20px",
    paddingRight: "20px",
    // marginTop: "10px",
  };
  // if(displayMode.value!== DisplayType.PDF){
  css["borderRadius"] = "5px";
  css["z-index:"] = "999 !important";
  // }
  return css;
});

// DELETE SECTION
const deleteSection = async (event: Event) => {
  event.stopPropagation(); 

  const result = await Sh.showGenericDialog({
    title: "Confirmar eliminación",
    message: "¿Estás seguro de que quieres eliminar esta sección?"
  });

  if (result) {
    const updatedItems = currentDesign.value.items.filter(item => item.id !== props.section.section.id);
    const finalItems = updatedItems.filter(item => item.parentItemId !== props.section.section.id);

    setDesign({
      ...currentDesign.value,
      items: currentDesign.value.items.filter(i =>  i.parentItemId !== props.section.section.id).filter(i => i.id !== props.section.section.id)
    });

    if (selectedSectionId.value === props.section.section.id) {
      selectItem('');
    }

  }
}


const isSectionEmpty = computed(() => props.section.items.length === 0);

const inputStyle = computed<CSSProperties>(() => {
  return {
    ...sectionStyle.value,
    height: '100%',
    minHeight: '40px',
    boxSizing: 'border-box',
    padding: '0 20px',
  };
});
  const isVisible = (section: FormDesignItemDto): boolean => {
    switch(displayMode.value) {
      case DisplayType.DISPLAY:
        if (section.properties && section.properties.screen && section.properties.screen.visible) {
          return true;
        }
        break;
      case DisplayType.EDIT:
        if (section.properties && section.properties.input && section.properties.input.visible) {
          return true;
        }
        break;
      case DisplayType.PDF:
        if (section.properties && section.properties.print && section.properties.print.visible) {
          return true;
        }
        break;
    }
    
    return false;
  };
  
</script>

<template>
  <div v-on:click="toggleSection" :style="sectionStyle" :class="{ 'is-flipped': isDeployed }">
    <div class="survey-section">
      <span v-if="!isEditingLabel || !isDesign" class="survey-section-title" v-on:click="onClickLabel">{{ section.label }}</span>
      <input v-if="isEditingLabel && isDesign" type="text" v-model="props.section.section.label"
        class="survey-item-label-input survey-section-title" :style="inputStyle" v-on:keyup="onKeyLabelEdit"
        v-on:blur="endEdit">
      <div class="sectionBannerBTNContainer">
        <button v-if="displayMode == DisplayType.EDIT && canDelete && isDesign" class="deleteSeccion-BTN" @click="deleteSection">
          <img class="survey-trashIcon" :src="trashIcon" alt="Icono de eliminar">
        </button>
        <img v-if="displayMode !== DisplayType.PDF" class="survey-bannerIcon" :src="bannerIcon"
          alt="Icono de survey-banner">
      </div>
    </div>
  </div>
  <BCollapse v-model="isDeployed">
    <template v-for="item in props.section.items">
      <SurveyItem v-if="isDesign || isVisible(item) || Sh.isDebug" v-on:click="selectItem(item.id)" :item="item" :response="item.response ?? null"/>
    </template>
  </BCollapse>
</template>

<script lang="ts">
// Error formula cuando se intruduce a mano, o ver si la etiquta se edita antes, no hace nada y da error en shared.
// PONER ZONA DE ARRASTRE DE IMAGEN UN GRIS MUY CLARO
export default {
  name: "SurveyItemSection",
}
</script>

<style lang="scss" scoped>
.survey-section {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
  height: 100%;
}

.survey-section-title {
  flex: 1;
  line-height: 40px;
  height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.survey-item-label-input {
  width: 100%;
  height: 100%;
  min-height: 40px;
  line-height: 40px;
  margin: 0;
  padding: 0 20px;
  box-sizing: border-box;
}

.sectionBannerBTNContainer {
  display: flex;
  align-items: center;
  height: 100%;
}

.deleteSeccion-BTN {
  display: flex;
  align-items: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  margin-right: 10px;
  padding: 0;
  height: 100%;
}

.survey-bannerIcon {
  height: 18px;
  width: auto;
}

.survey-trashIcon {
  height: 20px;
}
</style>